import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { mapTestResult, toTestRequest } from './actionMappers';
import type { TestProviderConfigInput, TestResult } from './actionTypes';
import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { useForgetRequest } from './forgetRequest';
import { testProviderConfigRequest } from './secretRequests';

export interface UseTestProviderConfigResult {
    /** Rejects with the raw error; `error` holds it parsed. */
    test: (input: TestProviderConfigInput) => Promise<TestResult>;
    isTesting: boolean;
    error: ProviderConfigError | null;
    /** Drops the request, typed secrets included, from the mutation cache. */
    reset: () => void;
}

/** Stores nothing, so nothing is invalidated. */
export function useTestProviderConfig(
    locationId: number,
    providerName: string
): UseTestProviderConfigResult {
    const { mutationKey, forget } = useForgetRequest('test');
    const mutation = useMutation({
        mutationKey,
        mutationFn: testProviderConfigRequest,
        // The variables, and error.response.config.data on a failure, carry typed secrets.
        // Without this the mutation keeps them in the cache for 5 minutes after unmount;
        // while mounted they live until reset.
        gcTime: 0,
    });
    const { mutateAsync } = mutation;

    const test = useCallback(
        async (input: TestProviderConfigInput) => {
            const response = await mutateAsync({
                locationId,
                providerName,
                requestBody: toTestRequest(input),
            });
            return mapTestResult(response.data);
        },
        [mutateAsync, locationId, providerName]
    );
    const error = useMemo(() => parsedErrorOrNull(mutation.error), [mutation.error]);
    const { reset: resetResult } = mutation;
    const reset = useCallback(() => {
        resetResult();
        forget();
    }, [resetResult, forget]);

    return { test, isTesting: mutation.isPending, error, reset };
}
